import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import ToggleSwitch from '@/components/ui/ToggleSwitch/ToggleSwitch';
import { RelationshipStatus } from '@/models/common/RelationshipStatus';
import { ChildStatusUpdateDto, ManagementChildDtoIn } from '@/models/in/ManagementChildDtoIn';
import { patchAuth } from '@/api/ApiCalls';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import styles from './StatusChangeDialog.module.css';

interface StatusChangeDialogProps {
  open: boolean;
  onClose: () => void;
  parentId: number;
  childId: number;
  newStatus: RelationshipStatus;
  onSuccess: (updatedData: ManagementChildDtoIn) => void;
}

const StatusChangeDialog = ({
  open,
  onClose,
  parentId,
  childId,
  newStatus,
  onSuccess,
}: StatusChangeDialogProps) => {
  const { t } = useTranslation();
  const { currentUser } = useAuthContext();
  const [note, setNote] = useState('');
  const [showInPersonalArea, setShowInPersonalArea] = useState(true);
  const [sendEmail, setSendEmail] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isRejection = newStatus === RelationshipStatus.Rejected;

  const handleSubmit = async () => {
    if (!currentUser?.token) return;

    // Note is required for rejection
    if (isRejection && !note.trim()) {
      setError(t('childrenManagement.noteRequired'));
      return;
    }

    setIsSubmitting(true);
    setError(null);

    const headers = { Authorization: `Bearer ${currentUser.token}` };
    const url = `management/children/${parentId}/${childId}/status`;
    const body: ChildStatusUpdateDto = {
      newStatus,
      note: note.trim() || undefined,
      showInPersonalArea,
      sendEmail,
    };

    try {
      const response = await patchAuth<ManagementChildDtoIn>(url, body, headers);
      onSuccess(response);
      setNote('');
    } catch (err) {
      console.error('Error updating child status:', err);
      setError(t('errorUpdatingStatus'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setNote('');
    setShowInPersonalArea(true);
    setSendEmail(false);
    setError(null);
    onClose();
  };

  return (
    <MainDialogLayout
      open={open}
      onOpenChange={(isOpen: boolean) => !isOpen && handleClose()}
      title={t('childrenManagement.changeStatus')}
    >
      <div className={styles.content}>
        <p className="body-md-regular text-(--font-body-1)">
          {t('childrenManagement.statusChangeConfirmation', {
            status: t(`childrenManagement.status.${newStatus}`)
          })}
        </p>

        <div className={styles.emailOption}>
          <ToggleSwitch
            label={t('officeComments.showInPersonalArea')}
            checked={showInPersonalArea}
            onChange={setShowInPersonalArea}
          />
          <ToggleSwitch
            label={t('sendEmailNotification')}
            checked={sendEmail}
            onChange={setSendEmail}
          />
        </div>

        <div className={styles.noteSection}>
          <label htmlFor="note" className="body-sm-semibold text-(--font-body-1)">
            {t('note')} {isRejection && '*'}
          </label>
          <textarea
            id="note"
            className={styles.textarea}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t('childrenManagement.notePlaceholder')}
            rows={3}
          />
          {sendEmail && (
            <p className="body-xs-sm-regular text-(--font-body-2)">
              {t('noteSentByEmail')}
            </p>
          )}
        </div>

        {error && (
          <p className={styles.error}>{error}</p>
        )}

        <div className={styles.actions}>
          <button
            className={styles.secondaryButton}
            onClick={handleClose}
            disabled={isSubmitting}
          >
            {t('close')}
          </button>
          <button
            className={styles.primaryButton}
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? t('loading') : sendEmail ? t('saveAndSend') : t('save')}
          </button>
        </div>
      </div>
    </MainDialogLayout>
  );
};

export default StatusChangeDialog;

